import { useState } from "react";
import { useOnline } from "@/hooks/localfirst/useOnline";
import { putLocalBlob } from "@/lib/localfirst/blobs";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { toast } from "sonner";

interface UseImageUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
  aspectRatio?: number; // 1 para cuadrado, 16/9 para widescreen, etc.
  /**
   * Sin conexión, guardar la imagen en el dispositivo y devolver una
   * referencia local (`local:…`) que se sube al enviar el cambio. Solo para
   * campos de colecciones disponibles sin conexión (lib/localfirst/blobs).
   */
  offlineLocal?: boolean;
}

export function useImageUpload(options: UseImageUploadOptions = {}) {
  const { maxWidth = 800, maxHeight = 600, quality = 0.8, offlineLocal = false } = options;
  const online = useOnline();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const generateUploadUrl = useMutation(
    api.companies.configuration.images.mutations.generateUploadUrl,
  );

  // Función para comprimir imagen
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Limpiar URL del objeto después de cargar
        URL.revokeObjectURL(img.src);

        // Calcular nuevas dimensiones manteniendo la relación de aspecto original
        let { width, height } = img;

        // Si se especifica aspectRatio, redimensionar manteniendo la relación de aspecto
        // pero sin recortar la imagen
        const targetAspectRatio = img.width / img.height; // Usar relación de aspecto original

        // Aplicar límites de tamaño máximo manteniendo la relación de aspecto
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // Redondear a números enteros para evitar problemas de renderizado
        width = Math.round(width);
        height = Math.round(height);

        canvas.width = width;
        canvas.height = height;

        // Dibujar imagen redimensionada manteniendo la relación de aspecto original
        ctx?.drawImage(img, 0, 0, width, height);

        // Convertir a blob con compresión
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/png",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          "image/png",
          quality,
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Función para validar archivo
  const validateFile = (file: File): string | null => {
    // Verificar tipo de archivo
    if (!file.type.startsWith("image/")) {
      return "Por favor selecciona un archivo de imagen válido";
    }

    // Verificar tamaño (antes de compresión)
    const maxSizeMB = 10; // 10MB máximo antes de compresión
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `El archivo es demasiado grande. Máximo ${maxSizeMB}MB`;
    }

    return null;
  };

  // Función principal de subida
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Validar archivo
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return null;
      }

      setUploadProgress(20);

      // Comprimir imagen
      const compressedFile = await compressImage(file);

      // Verificar tamaño después de compresión (sin toast)
      const finalSizeKB = compressedFile.size / 1024;

      setUploadProgress(50);

      // Sin red: queda en el dispositivo hasta enviar el cambio.
      if (offlineLocal && !online) {
        setUploadProgress(100);
        return await putLocalBlob(compressedFile);
      }

      // Obtener URL de subida
      const uploadUrl = await generateUploadUrl();
      setUploadProgress(70);

      // Subir archivo
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": compressedFile.type },
        body: compressedFile,
      });

      if (!result.ok) {
        throw new Error("Error al subir la imagen");
      }

      setUploadProgress(90);

      const { storageId } = await result.json();
      setUploadProgress(100);

      // Retornar storageId sin toast (el componente padre manejará el feedback)
      return storageId;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    uploadImage,
    isUploading,
    uploadProgress,
  };
}

/**
 * Sube varias imágenes en orden con UN solo toast: progreso mientras sube y
 * un resumen al final ("3 imágenes agregadas"). `onUploaded` recibe cada
 * storageId apenas sube, para ir mostrándolas. Los archivos que no son
 * imagen se saltan y se cuentan en el resumen.
 */
export async function uploadImageBatch(
  files: File[],
  upload: (file: File) => Promise<string | null>,
  onUploaded: (storageId: string) => void,
): Promise<void> {
  const images = files.filter((f) => f.type.startsWith("image/"));
  const skipped = files.length - images.length;
  if (images.length === 0) {
    toast.error("Solo se permiten archivos de imagen");
    return;
  }
  const many = images.length > 1;
  const toastId = toast.loading(many ? `Subiendo imágenes (0/${images.length})...` : "Subiendo imagen...");
  let ok = 0;
  for (const [i, file] of images.entries()) {
    if (many) toast.loading(`Subiendo imágenes (${i + 1}/${images.length})...`, { id: toastId });
    const storageId = await upload(file);
    if (storageId) {
      onUploaded(storageId);
      ok++;
    }
  }
  const notes = [
    images.length - ok > 0 ? `${images.length - ok} con error` : "",
    skipped > 0 ? `${skipped} no ${skipped === 1 ? "era imagen" : "eran imágenes"}` : "",
  ].filter(Boolean);
  if (ok === 0) {
    toast.error(many ? "No se pudo subir ninguna imagen" : "No se pudo subir la imagen", { id: toastId });
    return;
  }
  const done = ok === 1 ? "Imagen agregada" : `${ok} imágenes agregadas`;
  toast.success(notes.length ? `${done} (${notes.join(", ")})` : done, { id: toastId });
}
